/**
 * Shared left-rail filter primitives.
 *
 * A `RailItem` is a single-select filter row (icon + label + right-aligned
 * count) rendered as a real `<Link>` so right-click / open-in-new-tab work and
 * the active selection lives in the URL. The teal `.Mui-selected` treatment and
 * the `RAIL_SECTION_LABEL_SX` overline are the canonical rail look shared by the
 * Exceptions hub and the Platform Reports hub. An optional `endAdornment` sits
 * before the count for callers that want a right-edge signal (e.g. a band dot).
 */
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'

import { poolwerxColors } from '../../theme/colors'

// Muted section heading style for the rail's labels (AREA / TYPE / FAVOURITES).
// Indented to sit above the items' icon column.
export const RAIL_SECTION_LABEL_SX = {
  display: 'block',
  px: 1.5,
  pt: 0.5,
  pb: 0.25,
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: 0.8,
  lineHeight: 2,
  color: 'text.secondary',
} as const

export interface RailItemProps {
  to: string
  active: boolean
  icon: React.ReactNode
  label: string
  /** Right-aligned count (matched while searching, else total). */
  count: number
  /** Optional right-edge adornment rendered before the count (e.g. a band dot). */
  endAdornment?: React.ReactNode
}

export const RailItem: React.FC<RailItemProps> = ({
  to,
  active,
  icon,
  label,
  count,
  endAdornment,
}) => {
  return (
    <ListItemButton
      component={RouterLink}
      to={to}
      selected={active}
      sx={{
        borderRadius: 1.5,
        mb: 0.25,
        pl: 1.5,
        pr: 1.5,
        py: 0.75,
        minHeight: 44,
        transition: 'background-color 0.15s',
        '&.Mui-selected': {
          bgcolor: `${poolwerxColors.aqua}14`,
          '&:hover': { bgcolor: `${poolwerxColors.aqua}1F` },
          '& .rail-label': { color: poolwerxColors.teal, fontWeight: 700 },
          '& .rail-icon-color': { color: poolwerxColors.teal },
        },
        '&:hover': { bgcolor: 'grey.50' },
      }}
    >
      <ListItemIcon className="rail-icon-color" sx={{ minWidth: 32 }}>{icon}</ListItemIcon>
      <ListItemText
        disableTypography
        primary={
          <Typography
            className="rail-label"
            variant="body2"
            sx={{ fontWeight: 600, lineHeight: 1.3 }}
            noWrap
          >
            {label}
          </Typography>
        }
      />
      {/* Quiet right edge: an optional adornment (e.g. band dot) + the count. */}
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0, ml: 1 }}>
        {endAdornment}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
        >
          {count.toLocaleString()}
        </Typography>
      </Stack>
    </ListItemButton>
  )
}
